import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type {
  HybridEventLocation,
  InPersonEventLocation,
  VirtualEventLocation,
} from 'src/v3/core/types'
import type { HydratedSessionV3 } from '../events-v2.slice'
import type { EventV2 } from '../types'

dayjs.extend(utc)

let formatIcsDate = (isoString: string) =>
  `${dayjs.utc(isoString).format('YYYYMMDDTHHmmss')}Z`

// RFC 5545 §3.3.11: escape backslash, comma, semicolon, and newlines in TEXT values.
let escapeIcsText = (s: string) =>
  s
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/[\r\n]+/g, '\\n')

// RFC 5545 §3.1: fold content lines exceeding 75 octets.
// Continuation lines start with a single space (1 octet), so content per
// continuation chunk is capped at 74 to keep total line length ≤ 75.
let icsEncoder = new TextEncoder()
let foldIcsLine = (line: string): string =>
  Array.from(line)
    .reduce<string[]>(
      (chunks, ch) => {
        let last = chunks[chunks.length - 1]
        let candidate = `${last}${ch}`
        return icsEncoder.encode(candidate).length > 74
          ? [...chunks, ch]
          : [...chunks.slice(0, -1), candidate]
      },
      [''],
    )
    .join('\r\n ')

let formatIcsLocation = (
  location: VirtualEventLocation | InPersonEventLocation | HybridEventLocation,
): string | null => {
  if (location.kind === 'virtual') return location.url ?? null
  let addressParts = [
    location.address,
    location.city,
    location.state,
    location.zip,
  ].filter(Boolean)
  let address = addressParts.length ? addressParts.join(', ') : null
  if (location.kind === 'hybrid' && location.url) {
    return [address, location.url].filter(Boolean).join(' | ')
  }
  return address
}

export let generateIcsString = (
  session: HydratedSessionV3,
  event: EventV2,
): string => {
  let start = formatIcsDate(session.sessionTime)
  let end = formatIcsDate(
    session.endTime ??
      dayjs.utc(session.sessionTime).add(2, 'hour').toISOString(),
  )
  let summary = session.name ? `${event.name} – ${session.name}` : event.name
  let dtstamp = formatIcsDate(new Date().toISOString())
  let locationStr = formatIcsLocation(event.location)

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FoamfratStudio//Events//EN',
    'BEGIN:VEVENT',
    `UID:${session.id}@foamfratstudio`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    ...(event.description
      ? [`DESCRIPTION:${escapeIcsText(event.description)}`]
      : []),
    ...(locationStr ? [`LOCATION:${escapeIcsText(locationStr)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .map(foldIcsLine)
    .join('\r\n')
    .concat('\r\n')
}

export let downloadIcs = (session: HydratedSessionV3, event: EventV2) => {
  let content = generateIcsString(session, event)
  let blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  let url = URL.createObjectURL(blob)
  let a = document.createElement('a')
  let dateSuffix = dayjs.utc(session.sessionTime).format('YYYY-MM-DD')
  let slug =
    event.name.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_') || 'event'
  // eslint-disable-next-line fp/no-mutation
  a.href = url
  // eslint-disable-next-line fp/no-mutation
  a.download = `${slug}_${dateSuffix}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
